import { afterNextRender, ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { ListFiltersMemoryService } from 'src/app/shared/services/list-filters-memory.service';
import { CountriesService } from './core/services/countries.service';
import { CustomUIService } from './core/services/custom-ui.service';
import { LocationScopeService } from './core/services/location-scope.service';
import { LocationService } from './core/services/location.service';
import { NotificationService } from './core/services/notification.service';
import { PageTitleService } from './core/services/page-title.service';
import { RchService } from './core/services/rch.service';
import { UserService } from './core/services/user.service';
import { WebSocketService } from './core/services/websocket.service';
import { NgClass } from '@angular/common';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { dismissBootShell, revealAppFooter } from './core/layout/boot-shell';
import { LoadingService } from './core/layout/loader/loading.service';
import { FooterComponent } from './core/layout/footer.component';
import { AdvertisementBannerComponent } from './core/layout/advertisement-banner/advertisement-banner.component';
import { LoaderComponent } from './core/layout/loader/loader.component';

/**
 * A navigation shorter than this never raises the loader. Most are: the router only has to fetch a
 * chunk the first time a feature is visited, and a scrim that appears and vanishes on every click
 * reads as a stutter rather than as progress.
 */
const NAVIGATION_LOADER_DELAY_MS = 150;
const NAVIGATION_LOADER_ID = 'router-navigation';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [NgClass, RouterOutlet, FooterComponent, AdvertisementBannerComponent, LoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'wetaxi';

  //public appReady = new BehaviorSubject(false);
  public isLogged = new BehaviorSubject(false);

  oldLocation: any = null;
  oldFleet: any = null;

  private userService = inject(UserService);
  private locationService = inject(LocationService);
  private locationScope = inject(LocationScopeService);
  public customUIService = inject(CustomUIService);
  private rchService = inject(RchService);
  private countriesService = inject(CountriesService);
  private listFiltersMemory = inject(ListFiltersMemoryService);
  private webSocketService = inject(WebSocketService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  /** Constructed here for its effect: nothing else would keep the window title following the menu. */
  private pageTitleService = inject(PageTitleService);

  private navigationLoaderTimer: ReturnType<typeof setTimeout> | null = null;
  private navigationLoaderShown = false;

  constructor() {
    this.watchNavigation();
    this.notificationService.initService();
    // The socket room follows the data scope, and nothing else would construct that service early
    // enough to connect at boot.
    this.locationScope.initService();

    // this.webSocketService.socketListenContactCenter();

    // reset request memory filters
    this.listFiltersMemory.clearMemoryValues();

    // load countries json
    this.countriesService.loadCountries();

    this.customUIService.setHasCustomUI(true);
    this.customUIService.setDefaultUIStyle('wetaxi');
    this.customUIService.setStyles('wetaxi');
    // Once this component has painted, its footer is the real one — with the real version on it —
    // so the shell's stand-in band has nothing left to stand in for. (The shell's mark is painted
    // in `main.ts`, before bootstrap; nothing here is early enough to be worth doing it again.)
    afterNextRender(() => revealAppFooter());

    this.userService.currentUserChange.pipe(take(2)).subscribe((user) => {
      if (!user) {
        this.customUIService.getBanner();
        this.isLogged.next(false);
        //this.appReady.next(true);
        // If user is not logged in, takes the fleet from queryParams
        // es.: http://www.wetaxi.it/dashboard2?fleet=taxitorino
      } else {
        this.isLogged.next(true);

        //this.appReady.next(false);
        combineLatest(this.locationService.currentLocationChange, this.locationService.currentTaxiFleetChange)
          .pipe(
            map(([location, fleet], _i) => {
              if (!isEqual(this.oldLocation, location) || !isEqual(this.oldFleet, fleet)) {
                this.customUIService.checkFleetFromUser(user, fleet);
                this.customUIService.getBanner(fleet);
                this.oldLocation = location;
                this.oldFleet = fleet;
              }
              //this.appReady.next(true);

              return true;
            }),
            tap((_value) => {
              //this.appReady.next(value);
            })
          )
          .subscribe((_isReady) => {
            // leave the subscribe to enable pipe content
          });
      }
      this.webSocketService.initService();
    });

    this.userService.getUserFromStorage();
  }

  ngOnInit(): void {
    this.rchService.initializeService();
  }

  /**
   * Two things only the router can say: when the first screen is actually on the page — the boot
   * shell stands in for the layout until then — and when a navigation is taking long enough to be
   * worth reporting, a lazy route's chunk being a network fetch nothing else announces.
   */
  private watchNavigation(): void {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigationLoaderTimer = setTimeout(() => {
          this.navigationLoaderShown = true;
          this.loadingService.showLoader(NAVIGATION_LOADER_ID);
        }, NAVIGATION_LOADER_DELAY_MS);
        return;
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        this.settleNavigationLoader();
      }
      // Not on a cancel: that is the redirect the empty path performs on the way to the real
      // screen, and taking the shell down there would uncover the gap it exists to cover.
      if (event instanceof NavigationEnd || event instanceof NavigationError) {
        dismissBootShell();
      }
    });
  }

  private settleNavigationLoader(): void {
    if (this.navigationLoaderTimer) {
      clearTimeout(this.navigationLoaderTimer);
      this.navigationLoaderTimer = null;
    }
    // Only ever lower what this component raised: `hideLoader` on an empty queue lowers the loader
    // whoever put it up, and a request still in flight has its own reason to be holding it there.
    if (this.navigationLoaderShown) {
      this.navigationLoaderShown = false;
      this.loadingService.hideLoader(NAVIGATION_LOADER_ID);
    }
  }

  readonly hasCustomUI = toSignal(this.customUIService.hasCustomUI, { requireSync: true });
  readonly advBanner = toSignal(this.customUIService.advBanner, { requireSync: true });

  public get customUIS(): any {
    return this.customUIService;
  }

  public get customThemeName(): any {
    // remember to update the _custom_UI.scss file for new themes
    return this.customUIService.dynamicClass.value;
  }
}
